import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to detect when an element enters the viewport
 * and trigger a scroll reveal animation.
 * 
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - Percentage of element visible to trigger (default: 0.1)
 * @param {string} options.rootMargin - Margin around root element (default: '0px 0px -80px 0px')
 * @param {boolean} options.triggerOnce - Whether to trigger only once (default: true)
 * @returns {[React.RefObject, boolean]} - Ref to attach to element, and boolean indicating if it has revealed
 */
export function useScrollReveal(options = {}) {
  const {
    threshold = 0.05,
    rootMargin = '0px 0px -60px 0px',
    triggerOnce = true
  } = options;

  const [hasRevealed, setHasRevealed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    // Fallback for environments where IntersectionObserver might not trigger or is not supported
    if (typeof window === 'undefined' || !window.IntersectionObserver) {
      setHasRevealed(true);
      return;
    }

    // Safety measure for SEO, headless crawlers, and preview tools:
    // Reveal the content automatically after 1 second if it hasn't intersected yet.
    const timer = setTimeout(() => {
      setHasRevealed(true);
    }, 1000);

    const currentRef = ref.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasRevealed(true);
        clearTimeout(timer);
        if (triggerOnce) {
          observer.unobserve(entry.target);
        }
      } else if (!triggerOnce) {
        setHasRevealed(false);
      }
    }, {
      threshold,
      rootMargin,
    });

    observer.observe(currentRef);

    return () => {
      clearTimeout(timer);
      if (currentRef && !triggerOnce) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, hasRevealed];
}
